import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

// `filter` (optional): which fetched records may be offered -- e.g. no
// discarded tube to grow from. A record left out is still shown when it is
// the value already chosen.
export default function RecordAutocomplete({ fetchUrl, getLabel, getValue, getSecondary, value, onChange, onSelect, placeholder, filter }) {
  const { t } = useTranslation('common');
  const [records, setRecords] = useState([]);
  const [query, setQuery]     = useState('');
  const [open, setOpen]       = useState(false);
  const [loaded, setLoaded]   = useState(false);

  useEffect(() => {
    api.get(fetchUrl)
      .then(res => setRecords(res.data || []))
      .catch(() => setRecords([]))
      .finally(() => setLoaded(true));
  }, [fetchUrl]);

  useEffect(() => {
    if (!value) { setQuery(''); return; }
    const match = records.find(r => getValue(r) === value);
    if (match) setQuery(getLabel(match));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, records]);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    setOpen(true);
    if (value) onChange('');
  };

  const handleSelect = (record) => {
    setQuery(getLabel(record));
    onChange(getValue(record));
    if (onSelect) onSelect(record);
    setOpen(false);
  };

  const offered = filter ? records.filter(filter) : records;
  const filtered = (query
    ? offered.filter(r => getLabel(r)?.toLowerCase().includes(query.toLowerCase()))
    : offered
  ).slice(0, 20);

  return (
    <div style={{ position: 'relative' }}>
      <input
        value={query}
        onChange={handleInput}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        title={t('tip.recordSearch')}
        autoComplete="off"
        style={{ padding: '6px 10px', border: '1px solid var(--c-border-strong)', borderRadius: 6, fontSize: 14, width: '100%' }}
      />
      {open && filtered.length > 0 && (
        <ul style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'var(--c-surface)', border: '1px solid var(--c-border-strong)', borderRadius: 6,
          margin: 0, padding: 0, listStyle: 'none',
          maxHeight: 200, overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          {filtered.map(r => (
            <li key={getValue(r)}
              onMouseDown={() => handleSelect(r)}
              title={t('tip.recordOption')}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--c-bg-muted)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--c-primary-soft)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--c-surface)'}
            >
              {getLabel(r)}
              {getSecondary && getSecondary(r) && (
                <span style={{ fontSize: 11, color: 'var(--c-text-muted-fg)', marginLeft: 8 }}>{getSecondary(r)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && loaded && filtered.length === 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'var(--c-surface)', border: '1px solid var(--c-border-strong)', borderRadius: 6,
          padding: '8px 12px', fontSize: 12, color: 'var(--c-text-faint-fg)',
        }}>
          No matches
        </div>
      )}
    </div>
  );
}
